// EVERY LINK LANDS SIGNED IN — what the portal remembers between tabs so a
// link never asks someone who is already signed in to sign in again.
//
// The platform rule, Chauncey White 2026-10-23: "EVERY link emailed or sent or
// coded should check to see if the user is logged in.... its beyond
// exhausting having to authenticate EVERYTIME" — and 2026-11-02: "no matter
// waht app i go to, i am forced to Authenticate. there is no token stored?".
// Ported from ContractWISE (aims-contracts c9c94be, itself ExpenseWISE 0524e05
// + Helpdesk c188942 + the fleet's review findings): the link's person beats a
// cached account, the hint leaves the address bar once signed in, and a
// sign-out counts as a CHOICE only when the menu made it. CyberWISE sends no
// email, so there is no outbound half.
//
// The MSAL cache itself is localStorage now (msal.ts): a link opens a NEW tab,
// and sessionStorage belonged to one tab. Beside it, this keeps:
//   * who last signed in on this browser (localStorage) — the hint for the
//     automatic sign-in when a link carries none of its own;
//   * that they chose Sign out (localStorage) — never signed straight back in;
//   * that the one automatic redirect of this TAB was tried (sessionStorage) —
//     so a failed one cannot loop.
//
// The link's own hint is read by linkHint.ts. This module is pure on purpose
// (no window at import), so portal/test drives it over fake stores.
//
// ⚠ msal-browser 3.x (installed: 3.30): LOGIN_SUCCESS and ACQUIRE_TOKEN_SUCCESS
// BOTH carry an AuthenticationResult — the account is `payload.account`. 5.x
// differs (LOGIN_SUCCESS carries the AccountInfo itself); re-read
// StandardController.mjs before an upgrade. portal/test pins the shape.

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const LAST_UPN_KEY = 'cyberwise:last_upn';
export const SIGNED_OUT_KEY = 'cyberwise:signed_out';
/** One automatic sign-in redirect per tab; cleared when a sign-in succeeds. */
export const SSO_ATTEMPTED_KEY = 'wise_sso_attempted';

/** msal-browser's EventType values this module reads (the same strings in 3.x–5.x). */
export const LOGIN_SUCCESS = 'msal:loginSuccess';
export const ACQUIRE_TOKEN_SUCCESS = 'msal:acquireTokenSuccess';

function read(store: KeyValueStore | null, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(store: KeyValueStore | null, key: string, value: string | null): void {
  try {
    if (!store) return;
    if (value === null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    // Storage switched off: the portal still works, it just asks more often.
  }
}

function browserStore(which: 'localStorage' | 'sessionStorage'): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : (window[which] ?? null);
  } catch {
    return null;
  }
}

export interface SsoStores {
  local: KeyValueStore | null;
  session: KeyValueStore | null;
}

const browserStores = (): SsoStores => ({
  local: browserStore('localStorage'),
  session: browserStore('sessionStorage'),
});

/** A sign-in (or a token) succeeded: remember who, forget a chosen sign-out,
 *  and re-arm this tab's automatic sign-in. */
export function rememberSignedIn(upn: string | null | undefined, stores: SsoStores = browserStores()): void {
  if (upn) write(stores.local, LAST_UPN_KEY, upn);
  write(stores.local, SIGNED_OUT_KEY, null);
  write(stores.session, SSO_ATTEMPTED_KEY, null);
}

/** The person chose Sign out (the menu) — do not sign them straight back in. */
export function markSignedOut(stores: SsoStores = browserStores()): void {
  write(stores.local, SIGNED_OUT_KEY, '1');
}

export function signedOutOnPurpose(stores: SsoStores = browserStores()): boolean {
  return read(stores.local, SIGNED_OUT_KEY) === '1';
}

/** Who to sign in on arrival: the link's own hint (linkHint.ts), else whoever
 *  last signed in on this browser, else nobody — Entra then shows its own
 *  picker. */
export function arrivalLoginHint(linkHint: string | null, stores: SsoStores = browserStores()): string | undefined {
  return linkHint?.trim() || read(stores.local, LAST_UPN_KEY) || undefined;
}

/** Has this tab spent its one automatic redirect — or can it not keep the
 *  guard at all (no sessionStorage, or it throws)? A redirect without its
 *  guard could loop, so "cannot tell" counts as spent (PunchWISE §30). */
function autoSignInSpent(stores: SsoStores): boolean {
  if (!stores.session) return true;
  try {
    return stores.session.getItem(SSO_ATTEMPTED_KEY) !== null;
  } catch {
    return true;
  }
}

/** True once per tab: claim the one automatic sign-in redirect — only when
 *  the claim is really kept (it reads back). */
export function claimAutoSignIn(stores: SsoStores = browserStores()): boolean {
  if (autoSignInSpent(stores)) return false;
  try {
    stores.session!.setItem(SSO_ATTEMPTED_KEY, '1');
    return stores.session!.getItem(SSO_ATTEMPTED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Whether a tab with nobody signed in goes straight to Entra: not after a
 *  chosen sign-out, and not twice in one tab. */
export function shouldSignInAutomatically(stores: SsoStores = browserStores()): boolean {
  return !signedOutOnPurpose(stores) && !autoSignInSpent(stores);
}

/**
 * A link names its person; the browser may hold someone else. With the cache
 * in localStorage a shared PC still holds the last person who did not sign
 * out, and the page would open as THEM. So: the cached account the link
 * names, if there is one; otherwise, when other accounts are cached, sign in
 * as the named person. No hint, or nobody cached (the sign-in gate does
 * that), changes nothing.
 */
export function linkAccount<T extends { username: string }>(
  accounts: readonly T[],
  linkHint: string | null,
): { account: T | null; signInAs: string | null } {
  if (linkHint === null || linkHint.trim() === '' || accounts.length === 0) return { account: null, signInAs: null };
  const wanted = linkHint.trim().toLowerCase();
  const match = accounts.find((a) => a.username.toLowerCase() === wanted) ?? null;
  return match !== null ? { account: match, signInAs: null } : { account: null, signInAs: linkHint.trim() };
}

/**
 * The account this tab acts as: the ACTIVE one (the link's person, or whoever
 * just signed in), else the first cached. Every reader goes through here — a
 * bare `accounts[0]` on a shared PC is the last person who did not sign out.
 */
export function signedInAccount<T>(instance: { getActiveAccount(): T | null }, accounts: readonly T[]): T | undefined {
  return instance.getActiveAccount() ?? accounts[0];
}

/** The slice of an MSAL event this module reads. */
export interface SignInEvent {
  eventType: string;
  interactionType?: string | null;
  payload: unknown;
}

/**
 * The event callback (main.tsx). msal-browser 3.x: both events carry an
 * AuthenticationResult, so the account is `payload.account`. LOGIN_SUCCESS
 * fires only when the number of cached accounts grew; a redirect that signs in
 * an account the cache already held raises ACQUIRE_TOKEN_SUCCESS instead — so
 * a REDIRECT's ACQUIRE_TOKEN_SUCCESS makes its account active too. Whoever
 * just signed in is who this tab is, not the first cached.
 */
export function onSignInEvent<A extends { username: string }>(
  event: SignInEvent,
  instance: { setActiveAccount(account: A | null): void },
  stores: SsoStores = browserStores(),
): void {
  if (event.eventType !== LOGIN_SUCCESS && event.eventType !== ACQUIRE_TOKEN_SUCCESS) return;
  const account = ((event.payload as { account?: A | null } | null)?.account ?? null) as A | null;
  rememberSignedIn(account?.username, stores);
  if (account === null) return;
  if (event.eventType === LOGIN_SUCCESS || event.interactionType === 'redirect') instance.setActiveAccount(account);
}
